/**
 * Utility functions for file handling and formatting
 */

/**
 * Format file size from bytes to human-readable format
 * @param bytes - File size in bytes
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number, decimals: number = 2): string => {
	if (bytes === 0) return '0 Bytes';

	const k = 1024;
	const dm = decimals < 0 ? 0 : decimals;
	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

	const i = Math.floor(Math.log(bytes) / Math.log(k));

	return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * Format file size from MB to human-readable format
 * @param sizeInMB - File size in megabytes
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted file size string
 */
export const formatFileSizeFromMB = (sizeInMB: number, decimals: number = 1): string => {
	if (sizeInMB >= 1024) {
		return `${(sizeInMB / 1024).toFixed(decimals)}GB`;
	}
	return `${sizeInMB.toFixed(decimals)}MB`;
};

/**
 * Convert bytes to megabytes
 * @param bytes - File size in bytes
 * @returns Size in megabytes
 */
export const bytesToMB = (bytes: number): number => {
	return bytes / (1024 * 1024);
};

/**
 * Convert megabytes to bytes
 * @param mb - Size in megabytes
 * @returns Size in bytes
 */
export const mbToBytes = (mb: number): number => {
	return mb * 1024 * 1024;
};

/**
 * Validate file size against maximum allowed size
 * @param file - File to validate
 * @param maxSizeMB - Maximum size in megabytes
 * @returns Object with validation result and message
 */
export const validateFileSize = (file: File, maxSizeMB: number): { isValid: boolean; message?: string } => {
	const fileSizeMB = bytesToMB(file.size);
	
	if (fileSizeMB > maxSizeMB) {
		return {
			isValid: false,
			message: `File "${file.name}" (${formatFileSize(file.size)}) exceeds maximum size of ${formatFileSizeFromMB(maxSizeMB)}`
		};
	}
	
	return { isValid: true };
};

/**
 * Validate file extension against allowed extensions
 * @param file - File to validate
 * @param allowedExtensions - Array of allowed extensions (without dots)
 * @returns Object with validation result and message
 */
export const validateFileExtension = (file: File, allowedExtensions: string[]): { isValid: boolean; message?: string } => {
	if (allowedExtensions.length === 0) {
		return { isValid: true };
	}
	
	const extension = file.name.split('.').pop()?.toLowerCase();
	
	if (!extension || !allowedExtensions.includes(extension)) {
		return {
			isValid: false,
			message: `File "${file.name}" has an invalid extension. Allowed: ${allowedExtensions.join(', ')}`
		};
	}
	
	return { isValid: true };
};

/**
 * Calculate total size of multiple files
 * @param files - Array of files
 * @returns Total size in bytes
 */
export const calculateTotalFileSize = (files: File[]): number => {
	return files.reduce((total, file) => total + file.size, 0);
};

/**
 * Get file extension from filename
 * @param filename - Name of the file
 * @returns File extension (without dot) or empty string
 */
export const getFileExtension = (filename: string): string => {
	return filename.split('.').pop()?.toLowerCase() || '';
};

/**
 * Check if file is an image
 * @param file - File to check
 * @returns True if file is an image
 */
export const isImageFile = (file: File): boolean => {
	return file.type.startsWith('image/');
};

/**
 * Check if file is a video
 * @param file - File to check
 * @returns True if file is a video
 */
export const isVideoFile = (file: File): boolean => {
	return file.type.startsWith('video/');
};

/**
 * Check if file is a document (PDF, DOC, etc.)
 * @param file - File to check
 * @returns True if file is a document
 */
export const isDocumentFile = (file: File): boolean => {
	const documentTypes = [
		'application/pdf',
		'application/msword',
		'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
		'application/vnd.ms-excel',
		'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		'text/plain',
		'text/csv'
	];
	
	return documentTypes.includes(file.type);
};